import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { push } from 'react-router-redux';
import { Formik } from 'formik';
import moment from 'moment';

// Components
import ExtComp from '../../components/external';
import NewJobWrapper from '../../components/NewJobWrapper/NewJobWrapper';
import RadioBtnItem from '../../components/RadioEl/RadioBtnItem';
import DayRange from '../../components/DayRange/DayRange';

// Constants 
import { daysOfWeek, dayTimePeriodsList } from 'src/services/date'; // need for first setup default form parameters

// Actions
import { validation } from './validation';
import { setDetails } from '../../reducer/createNewJobActions';

// Style
import s from './NewJobReschedule.style';


const NewJobReschedule = (props) => {
  const { t, dispatch, specificDetails, campusesList } = props;

  const onConfirm = (values) => {
    dispatch(setDetails(values));
    dispatch(push('/practitioner/new-job/confirm'));
  };

  const allCampuses = () => {
    let allLoc = [];
    if (Array.isArray(campusesList)) {
      const locations = campusesList.map(c => ({ value: [c.id], name: c.name }));
      const allAvailable = { name: t('all_available_location'), value: campusesList.map(c => c.id) };
      allLoc = [allAvailable, ...locations];
    }
    return allLoc;
  };

  return (
    <Formik
      initialValues={specificDetails && specificDetails.hasOwnProperty('rescheduleDate') ?
        specificDetails
        : {
          rescheduleDate: moment().startOf('day'),
          appointmentType: 'original',
          appointmentTypeId: '',
          schedule: 'original',
          scheduleId: '',
          locationType: 'newByUser',
          locationsIds: allCampuses()[0] && allCampuses()[0].value,
          daysOfWeekType: 'newByUser',
          days: daysOfWeek,
          partOfDayType: 'newByUser',
          partOfDay: dayTimePeriodsList[0].value
        }}
      validate={validation}
      onSubmit={onConfirm}
    >{(formProps) => {
        const { values, errors, touched, handleChange, setFieldValue, handleSubmit, validateForm } = formProps;

        return (
          <NewJobWrapper
            screenTitle={t('reschedule')}
            percent={75}
            backPath='/practitioner/new-job/select-appointments'
            nextBtnAction={() => {
              validateForm().then(handleSubmit);
            }}
          >

            <s.DetailsWrapper>
              {/* ──── Date ────────────────────────────────────────────────────────────────────────────── */}
              <section className="section">
                <h3 className="section-title">{t('earliest_reschedule_date')}</h3>

                <ExtComp.DataPicker
                  label={t('select_date')}
                  selected={values.rescheduleDate}
                  dateFormat="DD.MM.YYYY dddd"
                  minDate={moment()}
                  onChange={(date) => { setFieldValue('rescheduleDate', date); }}
                />
              </section>

              {/* ──── Appointment type ─────────────────────────────────────────────────────────────────────── */}
              <section className="section">
                <h3 className="section-title">{t('resch_appointment_type')}</h3>

                <div className="radio-items-wrapper">
                  <RadioBtnItem
                    name="appointmentType"
                    id="appointmentType-0"
                    rightLabel={t('appoint_type_option_0')}
                    checked={values.appointmentType === 'original'}
                    value="original"
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    name="appointmentType"
                    id="appointmentType-1"
                    rightLabel={t('appoint_type_option_1')}
                    checked={values.appointmentType === 'another'}
                    value="another"
                    onChange={handleChange}
                  />
                </div>

                <ExtComp.InputField
                  label={t('enter_appoint_id')}
                  disabled={values.appointmentType === 'original'}
                  isRequired={false}
                  meta={{ touched: touched.appointmentTypeId, error: errors.appointmentTypeId }}
                  input={{
                    name: 'appointmentTypeId',
                    value: values.appointmentTypeId,
                    onChange: handleChange
                  }}
                />
              </section>


              {/* ──── Schedule type ─────────────────────────────────────────────────────────────────────── */}
              <section className="section">
                <h3 className="section-title">{t('schedule')}</h3>

                <div className="radio-items-wrapper">
                  <RadioBtnItem
                    name="schedule"
                    id="schedule-0"
                    rightLabel={t('schedule_option_0')}
                    checked={values.schedule === 'original'}
                    value='original'
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    name="schedule"
                    id="schedule-1"
                    rightLabel={t('schedule_option_1')}
                    checked={values.schedule === 'another'}
                    value='another'
                    onChange={handleChange}
                  />
                </div>

                <ExtComp.InputField
                  label={t('enter_schedule_id')}
                  disabled={values.schedule === 'original'}
                  isRequired={false}
                  meta={{ touched: touched.scheduleId, error: errors.scheduleId }}
                  input={{
                    name: 'scheduleId',
                    value: values.scheduleId,
                    onChange: handleChange
                  }}
                />
              </section>

              {/* ──── Appointment location ─────────────────────────────────────────────────────────────────── */}
              <section className="section">
                <h3 className="section-title">{t('appointment_location')}</h3>

                <div className="radio-items-wrapper">
                  <RadioBtnItem
                    name="locationType"
                    id="locationType-0"
                    value="original"
                    rightLabel={t('appointment_location_option_0')}
                    checked={values.locationType === 'original'}
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    name="locationType"
                    id="locationType-1"
                    value="newByUser"
                    rightLabel={t('appointment_location_option_1')}
                    checked={values.locationType === 'newByUser'}
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    disabled
                    name="locationType"
                    id="locationType-2"
                    value="newByPatient"
                    rightLabel={t('allow_patient_choose')}
                    checked={values.locationType === 'newByPatient'}
                    onChange={handleChange}
                  />
                </div>

                <ExtComp.SelectField
                  className="select-field-wrapper"
                  label={t('select_location')}
                  value={values.locationsIds}
                  data={allCampuses()}
                  valueField="value"
                  textField="name"
                  onChange={(campus) => { setFieldValue('locationsIds', campus.value); }}
                  input={{
                    disabled: values.locationType !== 'newByUser',
                  }}
                />
              </section>

              {/* ──── Appointment day of week ─────────────────────────────────────────────────────────── */}
              <section className="section">
                <h3 className="section-title">{t('appoint_day_of_week')}</h3>

                <div className="radio-items-wrapper">
                  <RadioBtnItem
                    name="daysOfWeekType"
                    id="daysOfWeekType-0"
                    value="original"
                    rightLabel={t('appoint_day_of_week_option_0')}
                    checked={values.daysOfWeekType === 'original'}
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    name="daysOfWeekType"
                    id="daysOfWeekType-1"
                    value="newByUser"
                    rightLabel={t('appoint_day_of_week_option_1')}
                    checked={values.daysOfWeekType === 'newByUser'}
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    disabled
                    name="daysOfWeekType"
                    id="daysOfWeekType-2"
                    value="newByPatient"
                    rightLabel={t('allow_patient_choose')}
                    checked={values.daysOfWeekType === 'newByPatient'}
                    onChange={handleChange}
                  />
                </div>

                <DayRange
                  disabled={values.daysOfWeekType !== 'newByUser'}
                  className="day-range-wrapper"
                  selectedDays={values.days}
                  onChange={daysList => { setFieldValue('days', daysList); }}
                />
              </section>


              {/* ──── Part of day ─────────────────────────────────────────────────────────────────── */}
              <section className="section">
                <h3 className="section-title">{t('appoint_part_of_day')}</h3>

                <div className="radio-items-wrapper">
                  <RadioBtnItem
                    name="partOfDayType"
                    id="partOfDayType-0"
                    value="original"
                    rightLabel={t('appoint_part_of_day_option_0')}
                    checked={values.partOfDayType === 'original'}
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    name="partOfDayType"
                    id="partOfDayType-1"
                    value="newByUser"
                    rightLabel={t('appoint_part_of_day_option_1')}
                    checked={values.partOfDayType === 'newByUser'}
                    onChange={handleChange}
                  />
                  <RadioBtnItem
                    disabled
                    name="partOfDayType"
                    id="partOfDayType-2"
                    value="newByPatient"
                    rightLabel={t('allow_patient_choose')}
                    checked={values.partOfDayType === 'newByPatient'}
                    onChange={handleChange}
                  />
                </div>

                <ExtComp.SelectField
                  dropUp
                  className="select-field-wrapper"
                  label={t('select_part_of_day')}
                  textField="title"
                  valueField="value"
                  value={values.partOfDay}
                  data={dayTimePeriodsList}
                  onChange={date => { setFieldValue('partOfDay', date.value); }}
                  input={{
                    disabled: values.partOfDayType !== 'newByUser',
                  }}
                />
              </section>

            </s.DetailsWrapper>
          </NewJobWrapper>);
      }
      }
    </Formik>
  );
};

const mapStateToProps = store => {
  const pr = store.practitioner;
  return {
    specificDetails: pr.createNewJob.specificDetails,
    campusesList: pr.common.campusesList
  };
};

export default translate()(
  connect(mapStateToProps)(NewJobReschedule)
);

